import { useCallback, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, ApiError, API_BASE } from "../api/client.js";
import { CSRF_HEADER, readCsrfToken } from "../api/csrf";
import { reposQueryKey, type Repo } from "./useRepos.js";
import { parseSseStream, parseSidebandPercent } from "./connectStream.js";

// ---------------------------------------------------------------------------
// Connections + repo listing (plain queries)
// ---------------------------------------------------------------------------

export interface GitConnection {
  id: string;
  provider: string;
  accountLogin: string;
}

export interface RemoteRepoOption {
  owner: string;
  name: string;
  defaultBranch: string;
  sizeKb: number;
  private: boolean;
  suggestedFilter: "blob:none" | "tree:0";
}

const enc = encodeURIComponent;

export function useGitConnections(slug: string) {
  return useQuery<{ connections: GitConnection[] }, ApiError>({
    queryKey: ["gitConnections", slug],
    queryFn: ({ signal }) =>
      apiFetch<{ connections: GitConnection[] }>(`/api/ws/${enc(slug)}/github/connections`, { signal }),
    enabled: slug.length > 0,
  });
}

export function useConnectionRepos(slug: string, connectionId: string | null) {
  return useQuery<{ repos: RemoteRepoOption[] }, ApiError>({
    queryKey: ["connectionRepos", slug, connectionId],
    queryFn: ({ signal }) =>
      apiFetch<{ repos: RemoteRepoOption[] }>(
        `/api/ws/${enc(slug)}/github/connections/${enc(connectionId!)}/repos`,
        { signal },
      ),
    enabled: slug.length > 0 && !!connectionId,
  });
}

// ---------------------------------------------------------------------------
// Streaming connect
// ---------------------------------------------------------------------------

export type ConnectPhase = "idle" | "cloning" | "done" | "error";

export interface ConnectSummary {
  supportedFileCount: number;
  historyDepth: number;
  headSha: string;
}

export interface ConnectInput {
  connectionId: string;
  owner: string;
  repo: string;
  slug: string;
  defaultBranch: string;
  pathPrefix?: string;
  cloneFilter?: "blob:none" | "tree:0";
}

export interface UseConnectRepo {
  phase: ConnectPhase;
  percent: number;
  lastLine: string;
  summary: ConnectSummary | null;
  repo: Repo | null;
  error: string | null;
  start: (input: ConnectInput) => Promise<void>;
  reset: () => void;
}

/**
 * Drives the streaming `POST …/github/connect` request: creates the repo, then
 * reads git sideband `progress` events to feed the bar, ending on `summary` +
 * `done` (or `error`). Relative URL → proxy-tolerant. On success the repo list
 * is invalidated so the new repo shows immediately.
 */
export function useConnectRepo(wsSlug: string): UseConnectRepo {
  const qc = useQueryClient();
  const [phase, setPhase] = useState<ConnectPhase>("idle");
  const [percent, setPercent] = useState(0);
  const [lastLine, setLastLine] = useState("");
  const [summary, setSummary] = useState<ConnectSummary | null>(null);
  const [repo, setRepo] = useState<Repo | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setPhase("idle");
    setPercent(0);
    setLastLine("");
    setSummary(null);
    setRepo(null);
    setError(null);
  }, []);

  const start = useCallback(
    async (input: ConnectInput) => {
      setPhase("cloning");
      setPercent(0);
      setLastLine("");
      setSummary(null);
      setRepo(null);
      setError(null);

      // Ensure the double-submit CSRF cookie exists before a POST (mirrors
      // apiFetch's bootstrap for cold loads).
      let token = readCsrfToken();
      if (!token) {
        await fetch(API_BASE + "/api/me", { credentials: "include" }).catch(() => undefined);
        token = readCsrfToken();
      }

      let res: Response;
      try {
        res = await fetch(`${API_BASE}/api/ws/${enc(wsSlug)}/github/connect`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream",
            ...(token ? { [CSRF_HEADER]: token } : {}),
          },
          body: JSON.stringify(input),
        });
      } catch {
        setError("network");
        setPhase("error");
        return;
      }

      // A pre-stream failure (validation, slug taken, missing connection) comes
      // back as a JSON error envelope, not a stream.
      if (!res.ok || !res.body) {
        const text = await res.text().catch(() => "");
        const code = jsonErrorCode(text);
        setError(code ?? `http_${res.status}`);
        setPhase("error");
        return;
      }

      let sawError = false;
      await parseSseStream(res.body, (ev) => {
        const data = safeParse(ev.data);
        switch (ev.event) {
          case "progress": {
            const line = typeof data?.line === "string" ? data.line : "";
            if (line) {
              setLastLine(line);
              const pct = parseSidebandPercent(line);
              if (pct !== null) setPercent((prev) => Math.max(prev, pct));
            }
            break;
          }
          case "summary":
            setSummary(data as ConnectSummary);
            setPercent(100);
            break;
          case "done":
            if (data?.repo) setRepo(data.repo as Repo);
            break;
          case "error":
            sawError = true;
            setError(typeof data?.message === "string" ? data.message : "clone_failed");
            break;
        }
      });

      if (sawError) {
        setPhase("error");
      } else {
        setPhase("done");
        void qc.invalidateQueries({ queryKey: reposQueryKey(wsSlug) });
      }
    },
    [wsSlug, qc],
  );

  return { phase, percent, lastLine, summary, repo, error, start, reset };
}

function safeParse(text: string): any {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function jsonErrorCode(text: string): string | null {
  const parsed = safeParse(text);
  return parsed?.error?.code ?? null;
}
